import React, { useState, useEffect } from 'react';
import { Trophy, Medal, Award, TrendingUp, X } from 'lucide-react';
import { getLeaderboard, getProfileRank, getLevelTitle } from '../../utils/gamificationManager';
import { playSound } from '../../utils/soundManager';

const Leaderboard = ({ currentProfileId, onClose }) => {
  const [leaderboard, setLeaderboard] = useState([]);
  const [currentRank, setCurrentRank] = useState(null);

  useEffect(() => {
    const data = getLeaderboard();
    setLeaderboard(data);
    setCurrentRank(getProfileRank(currentProfileId));
  }, [currentProfileId]);

  const handleClose = () => {
    playSound('click');
    onClose();
  };

  const getRankIcon = (rank) => {
    if (rank === 1) return <Trophy size={24} color="#FFD700" />;
    if (rank === 2) return <Medal size={24} color="#C0C0C0" />;
    if (rank === 3) return <Medal size={24} color="#CD7F32" />;
    return <Award size={20} color="#95a5a6" />;
  };

  const getRankClass = (rank) => {
    if (rank === 1) return 'rank-gold';
    if (rank === 2) return 'rank-silver';
    if (rank === 3) return 'rank-bronze';
    return '';
  };

  return (
    <div className="leaderboard-overlay" onClick={handleClose}>
      <div 
        className="leaderboard-modal animate-scaleIn" 
        onClick={(e) => e.stopPropagation()}
      >
        <div className="leaderboard-header">
          <div className="header-title">
            <Trophy size={28} color="#f39c12" />
            <h2>Leaderboard</h2>
          </div>
          <button className="leaderboard-close hover-scale" onClick={handleClose}>
            <X size={24} />
          </button>
        </div>

        {currentRank && (
          <div className="current-rank-banner">
            <TrendingUp size={20} />
            <span>Your Rank: <strong>#{currentRank}</strong></span>
          </div>
        )}

        <div className="leaderboard-content">
          {leaderboard.length === 0 ? (
            <div className="leaderboard-empty">
              <Trophy size={60} color="#95a5a6" />
              <h3>No Rankings Yet</h3>
              <p>Complete quizzes to appear on the leaderboard!</p>
            </div>
          ) : (
            <div className="leaderboard-list">
              {leaderboard.map((player, index) => {
                const rank = index + 1;
                const isCurrentUser = player.id === currentProfileId;
                const { title, color } = getLevelTitle(player.level);

                return (
                  <div 
                    key={player.id}
                    className={`leaderboard-item ${getRankClass(rank)} ${
                      isCurrentUser ? 'current-user' : ''
                    } animate-slideInLeft`}
                    style={{ animationDelay: `${index * 0.05}s` }}
                  >
                    <div className="rank-section">
                      <div className="rank-number">#{rank}</div>
                      <div className="rank-icon">{getRankIcon(rank)}</div>
                    </div>

                    <div className="player-avatar" style={{ background: player.color }}>
                      {player.avatar}
                    </div>

                    <div className="player-info">
                      <div className="player-name">
                        {player.name}
                        {isCurrentUser && <span className="you-badge">You</span>}
                      </div>
                      <div className="player-title" style={{ color: color }}>
                        {title} • Level {player.level}
                      </div>
                    </div>

                    <div className="player-stats">
                      <div className="stat-item">
                        <span className="stat-value">{player.totalXP.toLocaleString()}</span>
                        <span className="stat-label">XP</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{player.badges}</span>
                        <span className="stat-label">Badges</span>
                      </div>
                      <div className="stat-item">
                        <span className="stat-value">{player.quizzes}</span>
                        <span className="stat-label">Quizzes</span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="leaderboard-footer">
          <p>💡 Complete more quizzes to climb the ranks!</p>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;
